import clsx from "clsx";
import {
  FunctionComponent,
  type ReactNode,
  useId,
  useMemo,
  useRef,
} from "react";

import { packageNav } from "@story/data-centers-hub/config/packageNav";
import {
  deckSlides,
  type DeckSlideId,
} from "@story/data-centers-hub/config/slides";
import { useHubMotionEnabled } from "@story/data-centers-hub/hooks/useHubMotionEnabled";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { useHubShortViewport } from "@story/data-centers-hub/hooks/useHubShortViewport";
import { usePrefersReducedMotion } from "@story/data-centers-hub/hooks/usePrefersReducedMotion";

import { useFitNavLabelFont } from "./hooks/useFitNavLabelFont";
import * as styles from "./PackageNav.css";

/** Deck step chevron (pointed down). Prev rotates 180° for up. */
const STEP_CHEVRON_PATH = "M5 9 L12 16 L19 9";

/** Skip-to-intro: top bar + double chevron up. */
const INTRO_RETURN_BAR = "M6 5 L18 5";
const INTRO_RETURN_CHEVRON_A = "M6 12 L12 7 L18 12";
const INTRO_RETURN_CHEVRON_B = "M6 19 L12 14 L18 19";

/** Keep in sync with `stepIconPath` strokeWidth in PackageNav.css.ts. */
const STEP_STROKE_WIDTH = 2.25;

/** Same glyphs as Loudoun / Utah package-nav lede video toggle. */
function PauseIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="2" width="3.5" height="12" />
      <rect x="9.5" y="2" width="3.5" height="12" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 2.5v11L14 8 4 2.5Z" />
    </svg>
  );
}

function StepChevron({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg
      className={clsx(
        styles.stepIcon,
        direction === "prev" ? styles.stepIconPrev : styles.stepIconNext,
      )}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path className={styles.stepIconPath} d={STEP_CHEVRON_PATH} />
    </svg>
  );
}

function IntroReturnIcon() {
  return (
    <svg
      className={clsx(styles.stepIcon, styles.introReturnIcon)}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path className={styles.stepIconPath} d={INTRO_RETURN_BAR} />
      <path className={styles.stepIconPath} d={INTRO_RETURN_CHEVRON_A} />
      <path className={styles.stepIconPath} d={INTRO_RETURN_CHEVRON_B} />
    </svg>
  );
}

function PunchoutSurface({
  idle,
  children,
}: {
  idle?: boolean;
  children: ReactNode;
}) {
  const maskId = `package-nav-punch-${useId().replace(/:/g, "")}`;
  return (
    <svg
      className={clsx(styles.punchoutMask, idle && styles.punchoutMaskIdle)}
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse">
          <rect x="-2" y="-2" width="4096" height="4096" fill="white" />
          {children}
        </mask>
      </defs>
      <rect
        x="-2"
        y="-2"
        width="4096"
        height="4096"
        fill="currentColor"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

function PunchoutLabel({
  children,
  idle,
}: {
  children: string;
  idle?: boolean;
}) {
  return (
    <PunchoutSurface idle={idle}>
      <text
        className={styles.punchoutText}
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="black"
      >
        {children.toUpperCase()}
      </text>
    </PunchoutSurface>
  );
}

function PunchoutIconMask({
  children,
  viewBox = "0 0 16 16",
  center,
  size = 16,
}: {
  children: ReactNode;
  viewBox?: string;
  center?: [number, number];
  size?: number;
}) {
  const parts = viewBox.split(/[\s,]+/).map(Number);
  const vbWidth = parts[2] ?? 16;
  const vbHeight = parts[3] ?? 16;
  const [cx, cy] = center ?? [vbWidth / 2, vbHeight / 2];
  const scale = size / Math.max(vbWidth, vbHeight);
  return (
    <svg
      x="50%"
      y="50%"
      width={vbWidth * scale}
      height={vbHeight * scale}
      viewBox={viewBox}
      overflow="visible"
    >
      <g transform={`translate(${-cx} ${-cy})`} fill="black">
        {children}
      </g>
    </svg>
  );
}

interface Props {
  /** Live deck stop — used to enable/disable prev/next. */
  activeSlideId: DeckSlideId;
  /** When true, the deck leads with the editor’s note. */
  hasNote?: boolean;
  /** True while a wipe / hop lock is running — arrows ignore clicks. */
  stepBusy?: boolean;
  /** Same stepper as wheel / keyboard (`SlideDeck` `go`). */
  onStep?: (delta: -1 | 1) => void;
  /**
   * After a note deep-link (or mid-deck resume) skipped the title — show a
   * chrome control that reopens the intro. Independent of prev/next visibility
   * so short / motion-off viewports still get a way back.
   */
  showIntroReturn?: boolean;
  onIntroReturn?: () => void;
}

/**
 * Hub package chrome: title + prev/next + motion play/pause (Loudoun-style) +
 * Verge. No account cell — frees width for the full title when intro-return
 * slides open. Credits live on the closing slide.
 */
const PackageNav: FunctionComponent<Props> = ({
  activeSlideId,
  hasNote = false,
  stepBusy = false,
  onStep,
  showIntroReturn = false,
  onIntroReturn,
}) => {
  const [motionEnabled, setMotionEnabled] = useHubMotionEnabled();
  const prefersReducedMotion = usePrefersReducedMotion();
  const shortViewport = useHubShortViewport();
  const narrow = useHubNarrowViewport();
  const navRef = useRef<HTMLElement>(null);
  const homeLabelRef = useRef<HTMLSpanElement>(null);
  const titleTextRef = useRef<HTMLSpanElement>(null);
  const titleId = useId();
  // Title / The Verge share --package-nav-label-size. Fit against both labels
  // and the nav bar so a 360-wide stage can’t clip “THE VERGE”.
  useFitNavLabelFont(navRef, homeLabelRef, true, {
    alsoMeasureRef: titleTextRef,
  });
  // Short viewports force stacked layout — no snap mode to toggle into.
  const showMotionToggle = !prefersReducedMotion && !shortViewport;
  // Prev/next are desktop-only; narrow keeps swipe / the intro-return control.
  const showStepButtons = showMotionToggle && !narrow;
  const deck = useMemo(() => deckSlides(hasNote), [hasNote]);
  const slideIndex = deck.findIndex((slide) => slide.id === activeSlideId);
  const canStep =
    Boolean(onStep) && showStepButtons && motionEnabled && !stepBusy;
  const canPrev = canStep && slideIndex > 0;
  const canNext = canStep && slideIndex >= 0 && slideIndex < deck.length - 1;

  const titleHref = packageNav.titleHref;
  const titleText = (
    <span ref={titleTextRef} className={styles.titleText}>
      {packageNav.title}
    </span>
  );
  const title = (
    <p
      id={titleId}
      className={clsx(styles.title, titleHref && styles.cellPunchoutHover)}
    >
      {titleHref ? (
        <a className={styles.titleLink} href={titleHref}>
          {titleText}
        </a>
      ) : (
        titleText
      )}
      {titleHref ? (
        <PunchoutLabel idle>{packageNav.title}</PunchoutLabel>
      ) : null}
    </p>
  );

  const stepButton = (
    direction: "prev" | "next",
    enabled: boolean,
    delta: -1 | 1,
    label: string,
  ) => (
    <button
      type="button"
      className={clsx(
        styles.stepButton,
        styles.iconButton,
        styles.cellPunchoutHover,
      )}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!enabled) {
          return;
        }
        onStep?.(delta);
      }}
      disabled={!enabled}
      aria-label={label}
    >
      <StepChevron direction={direction} />
      <PunchoutSurface idle>
        <PunchoutIconMask viewBox="0 0 24 24" size={14} center={[12, 12]}>
          {direction === "prev" ? (
            <g transform="rotate(180 12 12)">
              <path
                d={STEP_CHEVRON_PATH}
                fill="none"
                stroke="black"
                strokeWidth={STEP_STROKE_WIDTH}
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
            </g>
          ) : (
            <path
              d={STEP_CHEVRON_PATH}
              fill="none"
              stroke="black"
              strokeWidth={STEP_STROKE_WIDTH}
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          )}
        </PunchoutIconMask>
      </PunchoutSurface>
    </button>
  );

  const introReturnEnabled =
    showIntroReturn && Boolean(onIntroReturn) && !stepBusy;
  // Keep mounted whenever the deck has a note so open/close can swipe the cell
  // instead of popping the control in and out of the tree.
  const introReturnButton = hasNote ? (
    <button
      type="button"
      className={clsx(
        styles.stepButton,
        styles.introReturnButton,
        styles.iconButton,
        styles.cellPunchoutHover,
      )}
      data-open={showIntroReturn ? "true" : "false"}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!introReturnEnabled) {
          return;
        }
        onIntroReturn?.();
      }}
      disabled={!introReturnEnabled}
      aria-hidden={!showIntroReturn}
      tabIndex={showIntroReturn ? undefined : -1}
      aria-label="Back to intro"
    >
      <span className={styles.introReturnFace}>
        <IntroReturnIcon />
        <PunchoutSurface idle>
          <PunchoutIconMask viewBox="0 0 24 24" size={14} center={[12, 12]}>
            <path
              d={INTRO_RETURN_BAR}
              fill="none"
              stroke="black"
              strokeWidth={STEP_STROKE_WIDTH}
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
            <path
              d={INTRO_RETURN_CHEVRON_A}
              fill="none"
              stroke="black"
              strokeWidth={STEP_STROKE_WIDTH}
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
            <path
              d={INTRO_RETURN_CHEVRON_B}
              fill="none"
              stroke="black"
              strokeWidth={STEP_STROKE_WIDTH}
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </PunchoutIconMask>
        </PunchoutSurface>
      </span>
    </button>
  ) : null;

  const showStepGroup = hasNote || showStepButtons;

  return (
    <nav
        ref={navRef}
        className={clsx(styles.nav, styles.pastLockup)}
        aria-labelledby={titleId}
      >
        <div className={styles.titleCluster}>
          {showStepGroup ? (
            <div className={styles.stepGroup}>
              {introReturnButton}
              {showStepButtons
                ? stepButton("prev", canPrev, -1, "Previous slide")
                : null}
              {showStepButtons
                ? stepButton("next", canNext, 1, "Next slide")
                : null}
            </div>
          ) : null}
          {title}
        </div>
        <div className={styles.row}>
          {showMotionToggle ? (
            <button
              type="button"
              className={clsx(
                styles.cell,
                styles.iconButton,
                styles.cellPunchoutHover,
              )}
              onClick={() => setMotionEnabled(!motionEnabled)}
              aria-label={
                motionEnabled
                  ? "Pause background motion"
                  : "Play background motion"
              }
              aria-pressed={motionEnabled}
            >
              {motionEnabled ? <PauseIcon /> : <PlayIcon />}
              <PunchoutSurface idle>
                <PunchoutIconMask>
                  {motionEnabled ? (
                    <>
                      <rect x="3" y="2" width="3.5" height="12" />
                      <rect x="9.5" y="2" width="3.5" height="12" />
                    </>
                  ) : (
                    <path d="M4 2.5v11L14 8 4 2.5Z" />
                  )}
                </PunchoutIconMask>
              </PunchoutSurface>
            </button>
          ) : null}
          <a
            className={clsx(styles.cell, styles.cellPunchoutHover)}
            href={packageNav.homeHref}
          >
            <span ref={homeLabelRef} className={styles.cellLabel}>
              {packageNav.homeLabel}
            </span>
            <PunchoutLabel idle>{packageNav.homeLabel}</PunchoutLabel>
          </a>
        </div>
    </nav>
  );
};

export default PackageNav;
